import React, { useCallback, useEffect, useState } from 'react';
import { Paths } from '../shared/url_helpers';
import AdminAccommodationDataFields, { IAccommodationDataFields } from '@reverbdotcom/commons/src/components/admin_accommodation_data_fields';
import AdminAccommodationAmountInputWithCurrencyConversion from '@reverbdotcom/commons/src/components/admin_accommodation_amount_input_with_currency_conversion';
import CsrfForm from '@reverbdotcom/commons/src/csrf_form';
import { RCAlertBox, RCButton, RCTextInput, RCIcon } from '@reverbdotcom/cadence/components';
import { InfoIcon } from '@reverbdotcom/cadence/icons/react';

const ORDER_ID_PATTERN = '[0-9\s]*';
const PERCENT_FEE_PATTERN = '[0-9.,-\s]*';
export const PAYMENT_CREDIT_LINE_ITEM_TYPE = 'payment_credit';

interface IProps {
  categoryToPolicyContextMap: object;
  categoryToReasonMap: object;
  defaultCategory: string;
  defaultValue: string;
  statementCurrency: string;
  statementId: number;
}

interface IAdminStatementLineItemFormData extends IAccommodationDataFields {
  amountOwed: string;
  description: string;
  orderId: string,
  percentFee: string;
  salePrice: string;
}

export function AdminStatementLineItemForm({
  categoryToPolicyContextMap,
  categoryToReasonMap,
  defaultCategory,
  defaultValue,
  statementId,
  statementCurrency,
}: IProps) {
  const [formData, setFormData] = useState<IAdminStatementLineItemFormData>({
    orderId: '',
    amountOwed: '',
    percentFee: '',
    salePrice: '',
    selectedCategory: '',
    selectedReason: '',
    additionalContext: '',
    selectedPolicyContexts: {},
    description: '',
  });
  const [ usdAmountEstimate, setUsdAmountEstimate ] = useState('');
  const [ usdSalePriceEstimate, setUsdSalePriceEstimate ] = useState('');

  const setFormField = useCallback((fieldName, value) => {
    setFormData((state) => ({
      ...state,
      [fieldName]: value,
    }));
  }, [ setFormData ]);

  const handleOrderIdChange = (event) => {
    setFormField('orderId', event.target.value);
  };

  const handleAmountChange = (event) => {
    setFormField('amountOwed', event.target.value);
  };

  const handleSalePriceChange = (event) => {
    setFormField('salePrice', event.target.value);
  };

  const handlePercentFeeChange = (event) => {
    setFormField('percentFee', event.target.value);
  };

  const handleDescriptionChange = (event) => {
    setFormField('description', event.target.value);
  };

  const isAccommodation = categoryToReasonMap[formData.selectedCategory]?.length > 0;

  useEffect(() => {
    if (defaultCategory) {
      setFormField('selectedCategory', defaultCategory);
    }
    if (defaultValue) {
      setFormField('amountOwed', defaultValue);
    }
  }, [ defaultCategory, defaultValue ]);

  const isPaymentCredit = formData.selectedCategory === PAYMENT_CREDIT_LINE_ITEM_TYPE;

  return (
    <div className="mt-4 mb-4">
      <CsrfForm
        action={Paths.adminCreateStatementLineItem.expand({ statementId })}
        method="POST"
      >
        <AdminAccommodationDataFields
          categoryLabel="Item Type"
          categoryToReasonMap={categoryToReasonMap}
          categoryToPolicyContextMap={categoryToPolicyContextMap}
          formData={formData}
          setFormField={setFormField}
        />

        {!isAccommodation && (
          <RCTextInput
            helpText="This description will appear on the seller's statement, and is necessary for non-accommodation line items. For accommodations (customer service, safe shipping claims, and disputes), the description will be auto-generated."
            id="description"
            label="Description"
            name="description"
            onChange={handleDescriptionChange}
            placeholder={isPaymentCredit ? 'Please describe how the payment was collected.' : ''}
            required
            value={formData.description}
          />
        )}

        <RCTextInput
          id="order_id"
          helpText="Order ID will be included in the line item description"
          label="Order ID"
          name="order_id"
          onChange={handleOrderIdChange}
          pattern={ORDER_ID_PATTERN}
          required={isAccommodation}
          value={formData.orderId}
        />

        <AdminAccommodationAmountInputWithCurrencyConversion
          id="sale_price"
          label="Sale Price"
          name="sale_price"
          onChange={handleSalePriceChange}
          userCurrency={statementCurrency}
          value={formData.salePrice}
          usdEstimate={usdSalePriceEstimate}
          setUsdEstimate={setUsdSalePriceEstimate}
          required={false}
        />

        <div className="size-120 mb-1">
          <RCIcon icon={<InfoIcon />} size="inline" spacing="right" />
          
          Fees billed to the seller are entered as positive amounts. Credits are entered as negative amounts.
          
        </div>
        <AdminAccommodationAmountInputWithCurrencyConversion
          id="amount_owed"
          label="Amount Owed"
          name="amount_owed"
          onChange={handleAmountChange}
          userCurrency={statementCurrency}
          value={formData.amountOwed}
          usdEstimate={usdAmountEstimate}
          setUsdEstimate={setUsdAmountEstimate}
        />

        <RCTextInput
          id="percent_fee"
          label="Percent Fee"
          name="percent_fee"
          onChange={handlePercentFeeChange}
          pattern={PERCENT_FEE_PATTERN}
          value={formData.percentFee}
        />

        {isPaymentCredit && (
          <div className="mb-4">
            <RCAlertBox
              type="warning"
            >
              <p>Please double check that the amount paid by the seller is equal to the credit amount entered above.</p>

              If the payment credit amount equals the remaining amount owed on the statement, the statement will be marked as paid.
            </RCAlertBox>
          </div>
        )}
        <RCButton
          isSubmit
          variant="filled"
        >
          Add Line Item
        </RCButton>
      </CsrfForm>
    </div>
  );
}

export default AdminStatementLineItemForm;
